import React from "react";
import { useDispatch } from "react-redux";
import { ThemeProvider } from "theme-ui";
import { Global, css } from "@emotion/core";
import { ROLES } from "@pacerpro/utils";
import {
  theme,
  CSSReset,
  GlobalStyles,
  AppHeader,
  useFetch,
  Flex,
  EllipsisLoader,
} from "@pacerpro/shared-ui";
import {
  Users,
  DistributionLists,
  setLocalSession,
  getSession,
  Offices,
} from "@pacerpro/api";
import Unauthorized from "../errors/Unauthorized";
import IEPage from "../errors/IEPage";
import { loadSession } from "../../reducers/session";
import { loadUsers, setUsersNavLoading } from "../../reducers/users";
import {
  setDistributionLists,
  setDistributionListsNavLoading,
} from "../../reducers/distributionLists";
import { setOffices } from "../../reducers/offices";
import { setTracks, setTracksNavLoading } from "../../reducers/tracks";

declare global {
  interface Document {
    documentMode?: any;
  }
}

const defaultDistributionListSort = "-activityAt";
const defaultOfficeSort = "name";

const Layout: React.FunctionComponent<{}> = ({ children }) => {
  const dispatch = useDispatch();
  const [officesResponse, officesLoading] = useFetch(Offices.getAll, {
    sort: defaultOfficeSort,
  });
  const [usersResponse, usersResponseLoading] = useFetch(Users.getAll, {
    include: ["supportedAttorneys", "assignedStaff"],
  });
  const [currentUser, isLoading] = useFetch(Users.getCurrent);
  const [session, sessionLoading] = useFetch(getSession);
  const [litigationsResponse, litigationsResponseLoading] = useFetch(
    DistributionLists.getAll,
    {
      filter: { status: "litigation" },
      sort: defaultDistributionListSort,
      include: ["case", "members"],
    },
  );
  const [tracksResponse, tracksResponseLoading] = useFetch(
    DistributionLists.getAll,
    {
      filter: { status: "tracks" },
      sort: defaultDistributionListSort,
      include: ["case", "members"],
    },
  );

  React.useEffect(() => {
    if (!isLoading && currentUser && !sessionLoading && session) {
      const { jwtToken, expiresAt } = session.data;
      dispatch(loadSession(currentUser));
      setLocalSession({ jwtToken, expiresAt });
    }
  }, [isLoading, currentUser, sessionLoading, session]);

  React.useEffect(() => {
    if (usersResponseLoading) {
      dispatch(setUsersNavLoading(true));
    }
    if (!usersResponseLoading && usersResponse) {
      dispatch(setUsersNavLoading(false));
      dispatch(
        loadUsers({
          ...usersResponse,
          filteredTotal: usersResponse.meta.recordCount,
        }),
      );
    }
  }, [usersResponse, usersResponseLoading]);

  React.useEffect(() => {
    if (litigationsResponseLoading) {
      dispatch(setDistributionListsNavLoading(true));
    }
    if (!litigationsResponseLoading && litigationsResponse) {
      dispatch(setDistributionListsNavLoading(false));
      dispatch(
        setDistributionLists({
          ...litigationsResponse,
          filteredTotal: litigationsResponse.meta.recordCount,
        }),
      );
    }
  }, [litigationsResponse, litigationsResponseLoading]);

  React.useEffect(() => {
    if (tracksResponseLoading) {
      dispatch(setTracksNavLoading(true));
    }
    if (!tracksResponseLoading && tracksResponse) {
      dispatch(setTracksNavLoading(false));
      dispatch(
        setTracks({
          ...tracksResponse,
          filteredTotal: tracksResponse.meta.recordCount,
        }),
      );
    }
  }, [tracksResponse, tracksResponseLoading]);

  React.useEffect(() => {
    if (!officesLoading && officesResponse) {
      dispatch(setOffices(officesResponse));
    }
  }, [officesLoading, officesResponse]);

  if (isLoading) {
    return (
      <Flex
        sx={{
          height: "100vh",
          width: "100vw",
        }}
        justify="center"
        align="center"
      >
        <EllipsisLoader size={18} color="#326ac6" />
      </Flex>
    );
  }

  const isIE = () => {
    const userAgent = window.navigator.userAgent;
    return !!document.documentMode || /MSIE | Trident/.test(userAgent);
  };

  if (isIE()) {
    return <IEPage />;
  }

  return (
    <ThemeProvider theme={theme}>
      <CSSReset />
      <GlobalStyles />
      <Global
        styles={css`
          :root {
            background: ${theme.colors.background};
          }
        `}
      />

      <AppHeader />
      {currentUser && currentUser.organization_role !== ROLES.OWNER ? (
        <Unauthorized />
      ) : (
        children
      )}
    </ThemeProvider>
  );
};

export default Layout;
